import { ui } from "@patient-watch/ui";
import { Button } from "@patient-watch/ui/button";
import { Input } from "@patient-watch/ui/input";
import { Label } from "@patient-watch/ui/label";
import dayjs from "dayjs";
import { PlusCircle, Trash2 } from "lucide-react";
import React from "react";
import { useFormContext } from "react-hook-form";

interface ArthrosamidLot {
    lot_number: string;
    expiry_date?: Date;
}

interface ArthrosamidLotsProps {
    name: string;
    label: string;
    description?: string;
}

const ArthrosamidLots = ({ name, label, description }: ArthrosamidLotsProps) => {
    const { watch, setValue } = useFormContext();
    const lots = (watch(name) as ArthrosamidLot[]) || [];

    const addLot = () => {
        setValue(name, [...lots, { lot_number: "", expiry_date: undefined }]);
    };

    const removeLot = (index: number) => {
        setValue(
            name,
            lots.filter((_, i) => i !== index)
        );
    };

    const updateLotNumber = (index: number, value: string) => {
        const newLots = [...lots];
        newLots[index] = { ...newLots[index], lot_number: value };
        setValue(name, newLots);
    };

    const updateExpiryDate = (index: number, value: string) => {
        const newLots = [...lots];
        newLots[index] = {
            ...newLots[index],
            expiry_date: value ? new Date(value) : undefined,
        };
        setValue(name, newLots);
    };

    return (
        <div className="space-y-4">
            <div>
                <Label className="font-semibold text-base">{label}</Label>
                {description && <p className="mt-1 text-muted-foreground text-sm">{description}</p>}
            </div>

            {lots.length > 0 ? (
                <div className="space-y-3">
                    {lots.map((lot, index) => (
                        <div key={index} className="flex items-end gap-3 p-3 border rounded-md">
                            <div className="flex-1">
                                <Label htmlFor={`${name}.${index}.lot_number`}>Lot Number</Label>
                                <Input
                                    id={`${name}.${index}.lot_number`}
                                    value={lot.lot_number}
                                    onChange={(e) => updateLotNumber(index, e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                            <div className="flex-1">
                                <Label htmlFor={`${name}.${index}.expiry_date`}>Expiry Date</Label>
                                <Input
                                    id={`${name}.${index}.expiry_date`}
                                    type="date"
                                    value={lot.expiry_date ? dayjs(lot.expiry_date).format("YYYY-MM-DD") : ""}
                                    onChange={(e) => updateExpiryDate(index, e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeLot(index)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                        </div>
                    ))}
                </div>
            ) : (
                <ui.Card className="bg-muted/50">
                    <ui.CardContent className="flex justify-center items-center py-6">
                        <p className="text-muted-foreground text-sm">No lot numbers added</p>
                    </ui.CardContent>
                </ui.Card>
            )}

            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addLot}>
                <PlusCircle className="mr-2 w-4 h-4" />
                Add Lot Number
            </Button>
        </div>
    );
};

export default ArthrosamidLots;
